import classNames from "classnames";
import * as React from "react";

import type {
  KnowledgeGraphSeed,
  KnowledgeGraphSeedSource,
  KnowledgeGraphTrace,
  KnowledgeGraphTraceOutcome,
  KnowledgeGraphTracePath,
  KnowledgeGraphTraceStage,
  KnowledgeGraphTraceStageStatus,
  TracedCandidate,
} from "@/shared/domain/admin/ChatDebugRecord";

import styles from "./KnowledgeGraphSection.module.css";
import { formatRuntime } from "./tokenFormat";

type BadgeTone = "good" | "warn" | "bad" | "neutral";

/** Small pill used for path/outcome/stage-status labels. Tone drives color —
 *  `bad` is reserved for the suppression outcomes and failed stages, the
 *  cases this view exists to make obvious. */
function Badge({ label, tone }: { label: string; tone: BadgeTone }) {
  return (
    <span className={styles.badge} data-tone={tone}>
      {label}
    </span>
  );
}

// Only `injected` puts text in front of the model — every other outcome
// names a gate that closed, so it gets the same "bad" tone as a failed
// stage rather than a graduated scale. That's the point of the view: a
// suppressed retrieval should look wrong, not merely different.
const OUTCOME_TONE: Record<KnowledgeGraphTraceOutcome, BadgeTone> = {
  injected: "good",
  no_seed_terms: "bad",
  no_candidates: "bad",
  empty_render: "bad",
  failed: "bad",
};

const STAGE_STATUS_TONE: Record<KnowledgeGraphTraceStageStatus, BadgeTone> = {
  ok: "good",
  empty: "neutral",
  fallback: "warn",
  skipped: "neutral",
  failed: "bad",
};

// User-facing names for the two read paths — "turn_context"/"tool_search" are
// internal identifiers, not something an admin unfamiliar with the pipeline
// would recognize at a glance.
const PATH_LABEL: Record<KnowledgeGraphTracePath, string> = {
  turn_context: "per-turn injection",
  tool_search: "explicit tool query",
};

// Human labels for each seed source, and the fixed order groups render in —
// roughly the priority order `build_seed_terms` assembles them in upstream,
// so the group that mattered most for ranking reads first.
const SEED_SOURCE_LABEL: Record<KnowledgeGraphSeedSource, string> = {
  latest_user_message: "Latest user message",
  latest_assistant_message: "Latest assistant message",
  active_task_title: "Active task — title",
  active_task_description: "Active task — description",
  tool_intent: "Tool-call intent",
  context_entity_name: "Attached context",
};

const SEED_SOURCE_ORDER: KnowledgeGraphSeedSource[] = [
  "latest_user_message",
  "latest_assistant_message",
  "active_task_title",
  "active_task_description",
  "tool_intent",
  "context_entity_name",
];

// Roughly the length of text three lines holds at the seed-text column's
// width — texts at or under this render in full, with no toggle to hide
// anything. Above it (typically the assistant message), the text starts
// clamped and needs a "Show more" click to read past the third line.
const SEED_TEXT_COLLAPSE_THRESHOLD = 180;

// Above this many prepared terms, the chip list starts collapsed — a pool
// this size is already more than fits on screen, so scanning it unfolded
// defeats the point of a debug view. Smaller pools stay open since there's
// nothing to hide.
const PREPARED_TERMS_COLLAPSE_THRESHOLD = 50;

function formatScore(value: number | null): string {
  return value === null ? "—" : value.toFixed(3);
}

function formatDetailValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

/**
 * A stage's `detail` dict as key/value pairs. This is where the literal
 * FalkorDB query string lives — the single most valuable field in the whole
 * view — so values render monospace and wrap instead of clipping.
 */
function StageDetail({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail);
  if (entries.length === 0) {
    return null;
  }
  return (
    <dl className={styles.detailList}>
      {entries.map(([key, value]) => (
        <div key={key} className={styles.detailRow}>
          <dt className={styles.detailKey}>{key}</dt>
          <dd className={styles.detailValue}>{formatDetailValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * A stage's candidate pool, already ranked — rendered in the given order,
 * not re-sorted. `rendered` rows (the ones that survived into the model's
 * prompt) get a highlighted background so the gap between a high-ranked
 * candidate and one the budget or a dedupe dropped is visible at a glance.
 */
function CandidatesTable({
  candidates,
  candidateCount,
}: {
  candidates: TracedCandidate[];
  candidateCount: number;
}) {
  if (candidates.length === 0) {
    return null;
  }
  // Only the rerank stage populates rerank_relevance — omit the column
  // entirely on every other stage rather than show a dash for every row.
  const hasRerank = candidates.some(
    (candidate) => candidate.rerank_relevance !== null,
  );
  return (
    <div className={styles.candidatesWrapper}>
      <table className={styles.candidatesTable}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Kind</th>
            <th className={styles.numeric}>Score</th>
            {hasRerank && <th className={styles.numeric}>Rerank</th>}
            <th className={styles.numeric}>Importance</th>
            <th className={styles.numeric}>Usage</th>
            <th>Convergent</th>
            <th>Contradicted</th>
            <th>Edge</th>
            <th>Rendered</th>
            <th>Preview</th>
          </tr>
        </thead>
        <tbody>
          {candidates.map((candidate, index) => (
            <tr
              key={`${candidate.name}-${index}`}
              data-rendered={candidate.rendered ? "true" : undefined}
            >
              <td>{candidate.name}</td>
              <td>{candidate.kind ?? "—"}</td>
              <td className={styles.numeric}>{formatScore(candidate.score)}</td>
              {hasRerank && (
                <td className={styles.numeric}>
                  {formatScore(candidate.rerank_relevance)}
                </td>
              )}
              <td className={styles.numeric}>
                {formatScore(candidate.importance)}
              </td>
              <td className={styles.numeric}>
                {formatScore(candidate.usage_score)}
              </td>
              <td>{candidate.convergent ? "✓" : ""}</td>
              <td>{candidate.contradicted ? "✓" : ""}</td>
              <td className={styles.edge}>{candidate.edge ?? "—"}</td>
              <td>{candidate.rendered ? "✓" : ""}</td>
              <td className={styles.preview}>
                {candidate.content_preview ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {candidates.length < candidateCount && (
        <div className={styles.capNote}>
          showing {candidates.length} of {candidateCount}
        </div>
      )}
    </div>
  );
}

/** One collapsible stage row: name, status, timing, and its detail/candidates
 *  once expanded. Collapsed by default — the trace-level summary already
 *  surfaces the outcome that matters most. */
function StageRow({ stage }: { stage: KnowledgeGraphTraceStage }) {
  return (
    <details className={styles.stage}>
      <summary className={styles.stageSummary}>
        <span className={styles.stageName}>{stage.name}</span>
        <Badge label={stage.status} tone={STAGE_STATUS_TONE[stage.status]} />
        <span className={styles.stageMeta}>
          {formatRuntime(stage.duration_ms)} · {stage.candidate_count} candidate
          {stage.candidate_count === 1 ? "" : "s"}
        </span>
      </summary>
      <StageDetail detail={stage.detail} />
      <CandidatesTable
        candidates={stage.candidates}
        candidateCount={stage.candidate_count}
      />
    </details>
  );
}

/**
 * Legacy fallback for traces recorded before `seeds` shipped: a flat,
 * priority-ordered list of raw terms with no source or usage metadata.
 * Element 0 is labeled primary — it's the latest user message, and what the
 * reranker ranks against — the same thing the `latest_user_message` group
 * means in the richer `seeds` view, just without the shape to say so
 * directly.
 */
function LegacySeedTerms({ seedTerms }: { seedTerms: string[] }) {
  if (seedTerms.length === 0) {
    return null;
  }
  return (
    <div className={styles.termRow}>
      <span className={styles.termLabel}>Seed terms:</span>
      {seedTerms.map((term, index) => (
        <span key={index} className={styles.term}>
          {term}
          {index === 0 && <span className={styles.primaryTag}> (primary)</span>}
        </span>
      ))}
    </div>
  );
}

/**
 * One seed's text, collapsed to ~3 lines with a "Show more" toggle once it's
 * long enough to need one — the latest assistant message is routinely long
 * enough to swamp the shorter seeds around it otherwise. Short seeds (most
 * of the other sources) render in full with no toggle at all.
 */
function SeedText({ text }: { text: string }) {
  const [expanded, setExpanded] = React.useState(false);
  const isLong = text.length > SEED_TEXT_COLLAPSE_THRESHOLD;
  return (
    <div className={styles.seedTextWrapper}>
      <p
        className={classNames(styles.seedText, {
          [styles.seedTextClamped]: isLong && !expanded,
        })}
      >
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          className={styles.toggle}
          onClick={() => {
            setExpanded((previous) => !previous);
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}

/** Groups seeds by source, preserving each group's original relative order. */
function groupSeedsBySource(
  seeds: KnowledgeGraphSeed[],
): Map<string, KnowledgeGraphSeed[]> {
  const groups = new Map<string, KnowledgeGraphSeed[]>();
  for (const seed of seeds) {
    const group = groups.get(seed.source);
    if (group) {
      group.push(seed);
    } else {
      groups.set(seed.source, [seed]);
    }
  }
  return groups;
}

interface SeedGroup {
  source: string;
  label: string;
  /** Whether this client knows the source, i.e. it is in `SEED_SOURCE_ORDER`. */
  recognized: boolean;
  seeds: KnowledgeGraphSeed[];
}

/**
 * Seed groups in render order: the sources this client knows, in
 * `SEED_SOURCE_ORDER`, then anything left over.
 *
 * The leftover pass is the point. Rendering only known sources meant a source
 * added server-side vanished here — no group, no count mismatch, no warning —
 * from the one view whose job is explaining what retrieval saw. An unknown
 * source now renders under its raw name, flagged, so the gap is visible
 * instead of invisible.
 */
function orderSeedGroups(seeds: KnowledgeGraphSeed[]): SeedGroup[] {
  const grouped = groupSeedsBySource(seeds);
  const groups: SeedGroup[] = [];
  for (const source of SEED_SOURCE_ORDER) {
    const group = grouped.get(source);
    if (group) {
      groups.push({
        source,
        label: SEED_SOURCE_LABEL[source],
        recognized: true,
        seeds: group,
      });
      grouped.delete(source);
    }
  }
  for (const [source, group] of grouped) {
    groups.push({
      source,
      label: `Unrecognized source: ${source}`,
      recognized: false,
      seeds: group,
    });
  }
  return groups;
}

/**
 * Seeds grouped under a human-labeled heading per source (see
 * `orderSeedGroups`). Each seed shows a badge naming how it was used —
 * `embedded + full-text` when it was also vectorized, `full-text only`
 * otherwise — so which seeds fed vector search is visible without
 * cross-referencing the stage detail.
 */
function SeedsBySource({ seeds }: { seeds: KnowledgeGraphSeed[] }) {
  return (
    <div className={styles.seedsSection}>
      {orderSeedGroups(seeds).map((group) => (
        <div key={group.source} className={styles.seedGroup}>
          <div className={styles.seedGroupLabel}>
            {group.label}
            {!group.recognized && (
              <Badge label="unknown to this UI" tone="bad" />
            )}
          </div>
          {group.seeds.map((seed, index) => (
            <div key={index} className={styles.seedRow}>
              <Badge
                label={
                  seed.vector_seed ? "embedded + full-text" : "full-text only"
                }
                tone={seed.vector_seed ? "good" : "neutral"}
              />
              <SeedText text={seed.text} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * The trace's seeds, in whichever shape it was recorded in: the grouped,
 * source-attributed view when `seeds` is present, or the flat legacy list
 * built from `seed_terms` for traces recorded before `seeds` shipped.
 */
function SeedsSection({ trace }: { trace: KnowledgeGraphTrace }) {
  if (trace.seeds && trace.seeds.length > 0) {
    return <SeedsBySource seeds={trace.seeds} />;
  }
  return <LegacySeedTerms seedTerms={trace.seed_terms} />;
}

/**
 * Post-tokenization terms actually searched, as wrapped monospace chips
 * behind a count. An empty pool alongside non-empty seeds is called out
 * explicitly — it's the `no_seed_terms` gate, the first thing to suspect
 * when nothing surfaced. A pool over
 * `PREPARED_TERMS_COLLAPSE_THRESHOLD` starts collapsed, since scanning 50+
 * chips unfolded defeats the point of a debug view.
 */
function PreparedTerms({ preparedTerms }: { preparedTerms: string[] }) {
  if (preparedTerms.length === 0) {
    return (
      <div className={styles.termRow}>
        <span className={styles.termLabel}>Prepared terms:</span>
        <span className={styles.gateNote}>
          none — seeds tokenized to nothing (no_seed_terms gate)
        </span>
      </div>
    );
  }
  return (
    <details
      className={styles.preparedTerms}
      open={preparedTerms.length <= PREPARED_TERMS_COLLAPSE_THRESHOLD}
    >
      <summary className={styles.preparedTermsSummary}>
        Prepared terms ({preparedTerms.length})
      </summary>
      <div className={styles.termChips}>
        {preparedTerms.map((term, index) => (
          <span key={index} className={styles.termChip}>
            {term}
          </span>
        ))}
      </div>
    </details>
  );
}

function TraceTextBlock({ label, text }: { label: string; text: string }) {
  return (
    <div className={styles.blockWrapper}>
      <div className={styles.blockLabel}>{label}</div>
      <pre className={styles.block}>{text}</pre>
    </div>
  );
}

/**
 * One retrieval trace: header badges (path, outcome, timing, reranker),
 * seed/prepared terms, every stage the pipeline ran, any CONTRADICTS
 * warnings, and the text the model actually saw. Expanded by default when
 * the outcome isn't `injected` — that's the "why did memory not surface"
 * case, and it shouldn't take an extra click to see why.
 */
function TraceRow({ trace }: { trace: KnowledgeGraphTrace }) {
  return (
    <details className={styles.trace} open={trace.outcome !== "injected"}>
      <summary className={styles.traceSummary}>
        <Badge label={PATH_LABEL[trace.path]} tone="neutral" />
        <Badge label={trace.outcome} tone={OUTCOME_TONE[trace.outcome]} />
        <span className={styles.traceMeta}>
          {formatRuntime(trace.duration_ms)} ·{" "}
          {`reranker ${trace.reranker_used ? "used" : "not used"}`}
        </span>
      </summary>
      <div className={styles.traceBody}>
        <SeedsSection trace={trace} />
        <PreparedTerms preparedTerms={trace.prepared_terms} />
        {trace.stages.map((stage, index) => (
          <StageRow key={`${stage.name}-${index}`} stage={stage} />
        ))}
        {trace.warnings.length > 0 && (
          <div className={styles.warnings}>
            {trace.warnings.map((warning, index) => (
              <div key={index} className={styles.warning}>
                {warning}
              </div>
            ))}
          </div>
        )}
        {trace.preferences_block && (
          <TraceTextBlock
            label="Preferences block"
            text={trace.preferences_block}
          />
        )}
        {trace.injected_block ? (
          <TraceTextBlock
            label="Injected block (what the model saw)"
            text={trace.injected_block}
          />
        ) : (
          <div className={styles.gateNote}>
            Nothing was injected into the prompt for this retrieval.
          </div>
        )}
      </div>
    </details>
  );
}

export interface KnowledgeGraphSectionProps {
  /** Knowledge-graph retrieval traces recorded for this turn, in the order
   *  they ran — the passive turn-context injection plus any
   *  `query_knowledge` tool calls. Empty on turns recorded before
   *  capture shipped. */
  traces: KnowledgeGraphTrace[];
}

/**
 * Collapsible "Knowledge Graph" section: renders inside
 * `MessageKnowledgeGraphButton`'s dialog, opened from a user message in the
 * admin thread explorer. One sub-section per retrieval trace shows the seed
 * terms, every stage the pipeline ran (with the literal query text each one
 * sent and the candidates it produced), any CONTRADICTS warnings, and the
 * text the model actually saw — enough to answer, stage by stage, why the
 * right memory did or didn't surface.
 */
export function KnowledgeGraphSection({ traces }: KnowledgeGraphSectionProps) {
  return (
    <details className={styles.section}>
      <summary className={styles.sectionSummary}>
        Knowledge Graph ({traces.length})
      </summary>
      <div className={styles.sectionBody}>
        {traces.length === 0 ? (
          <div className={styles.gateNote}>
            No knowledge-graph retrieval — neither the per-turn injection nor a
            query_knowledge tool call — was recorded for any of this turn&apos;s
            Bedrock calls. Either the turn predates capture, or none ran.
          </div>
        ) : (
          traces.map((trace, index) => <TraceRow key={index} trace={trace} />)
        )}
      </div>
    </details>
  );
}
